import React, { forwardRef, useEffect, useState } from "react";
import MaterialTable from "material-table";
import { ToastContainer, toast } from "react-toastify";
import {
  ArrowDownward,
  Check,
  ChevronLeft,
  ChevronRight,
  Clear,
  DeleteOutline,
  Edit,
  FilterList,
  FirstPage,
  LastPage,
  Remove,
  SaveAlt,
  Search,
  ViewColumn,
} from "@material-ui/icons";
import { TextField } from "@material-ui/core";
import { Container, Button, Card } from "react-bootstrap";
import axios from "../Components/Axios/Axios";
// Custom Components Import
import Loader from "../Components/Spinner/Spinner";
import Sidebar from "../Components/Sidebar/MainSidebar/Sidebar";
import requests from "../Components/Requests/Requests";

// ---- Rows-per-page choices (kept in sync with the backend's
// ALLOWED_PAGE_SIZES whitelist in getuserinterest) ----
const PAGE_SIZE_OPTIONS = [30, 60, 90, 120];

function UserInterest() {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [page, setPage] = useState(0); // MaterialTable uses 0-based page indexing
  const [pageSize, setPageSize] = useState(30); // rows per page, one of PAGE_SIZE_OPTIONS
  const [totalCount, setTotalCount] = useState(0);

  // ---- Export all state ----
  const [exportingAll, setExportingAll] = useState(false);

  // ---- Filter state ----
  const [filters, setFilters] = useState({
    country: "",
    platform: "",
    language: "",
    fromDate: "",
    toDate: "",
  });

  // ---- Filter dropdown options (from API) ----
  const [countryOptions, setCountryOptions] = useState([]);
  const [platformOptions, setPlatformOptions] = useState([]);
  const [languageOptions, setLanguageOptions] = useState([]);

  const tableIcons = {
    Check: forwardRef((props, ref) => <Check {...props} ref={ref} />),
    Clear: forwardRef((props, ref) => <Clear {...props} ref={ref} />),
    Delete: forwardRef((props, ref) => <DeleteOutline {...props} ref={ref} />),
    DetailPanel: forwardRef((props, ref) => (
      <ChevronRight {...props} ref={ref} />
    )),
    Edit: forwardRef((props, ref) => <Edit {...props} ref={ref} />),
    Export: forwardRef((props, ref) => <SaveAlt {...props} ref={ref} />),
    Filter: forwardRef((props, ref) => <FilterList {...props} ref={ref} />),
    FirstPage: forwardRef((props, ref) => <FirstPage {...props} ref={ref} />),
    LastPage: forwardRef((props, ref) => <LastPage {...props} ref={ref} />),
    NextPage: forwardRef((props, ref) => <ChevronRight {...props} ref={ref} />),
    PreviousPage: forwardRef((props, ref) => (
      <ChevronLeft {...props} ref={ref} />
    )),
    ResetSearch: forwardRef((props, ref) => <Clear {...props} ref={ref} />),
    Search: forwardRef((props, ref) => <Search {...props} ref={ref} />),
    SortArrow: forwardRef((props, ref) => (
      <ArrowDownward {...props} ref={ref} />
    )),
    ThirdStateCheck: forwardRef((props, ref) => (
      <Remove {...props} ref={ref} />
    )),
    ViewColumn: forwardRef((props, ref) => <ViewColumn {...props} ref={ref} />),
  };

  useEffect(() => {
    fetchData(page + 1, filters);
  }, [page, pageSize]);

  // ---- Fetch data with filters ----
  const fetchData = async (currentPage, currentFilters = filters) => {
    setLoading(true);
    try {
      const response = await axios.get(requests.allUserInterest, {
        params: {
          page: currentPage,
          pageSize: pageSize,
          country: currentFilters.country,
          platform: currentFilters.platform,
          language: currentFilters.language,
          fromDate: currentFilters.fromDate,
          toDate: currentFilters.toDate,
        },
      });
      if (response.data.Success === 1) {
        setData(response.data.data);
        setTotalCount(response.data.totalCount);

        // ---- Populate filter dropdown options from API response ----
        if (response.data.filterOptions) {
          setCountryOptions(response.data.filterOptions.countries || []);
          setPlatformOptions(response.data.filterOptions.platforms || []);
          setLanguageOptions(response.data.filterOptions.languages || []);
        }
      } else {
        toast.warning(`Something went wrong`, {
          position: "top-right",
          autoClose: 2500,
        });
      }
    } catch (err) {
      toast.warning(err.message, {
        position: "top-right",
        autoClose: 2500,
      });
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (newPage) => {
    setPage(newPage);
  };

  const handlePageSizeChange = (newPageSize) => {
    setPageSize(newPageSize);
    setPage(0); // rows-per-page changed, go back to page 1
  };

  // ---- CSV helpers ----
  const csvEscape = (val) => {
    if (val === null || val === undefined) return "";
    const str = String(val);
    if (/[",\n\r]/.test(str)) {
      return `"${str.replace(/"/g, '""')}"`;
    }
    return str;
  };

  // ---- Export ALL filtered records ----
  // Hit allUserInterest once WITHOUT a `page` param -- the backend treats a
  // missing page as "return every matching row, unpaginated" (see
  // isPaginated in getuserinterest). One request, one DB query, then just
  // convert the result to CSV.
  const exportAllData = async (columnList) => {
    if (exportingAll) return;
    setExportingAll(true);

    const params = {
      country: filters.country,
      platform: filters.platform,
      language: filters.language,
      fromDate: filters.fromDate,
      toDate: filters.toDate,
    };

    try {
      const response = await axios.get(requests.allUserInterest, { params });

      if (response.data.Success !== 1) {
        toast.warning("Export failed. Could not fetch records.", {
          position: "top-right",
          autoClose: 2500,
        });
        return;
      }

      const rows = response.data.data || [];

      if (rows.length === 0) {
        toast.info("No records match these filters.", {
          position: "top-right",
          autoClose: 2500,
        });
        return;
      }

      // Only columns that map to real data get exported; action/button
      // columns (if any) are skipped via exportSkip.
      const exportableCols = (columnList || []).filter(
        (col) => col.field && !col.exportSkip,
      );
      const headerLine = exportableCols
        .map((col) => csvEscape(col.title))
        .join(",");

      const lines = [headerLine];
      for (const row of rows) {
        const line = exportableCols
          .map((col) => csvEscape(row[col.field]))
          .join(",");
        lines.push(line);
      }
      const totalRows = rows.length;

      // single join, BOM so Excel reads UTF-8 correctly
      const blob = new Blob(["﻿" + lines.join("\n")], {
        type: "text/csv;charset=utf-8;",
      });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `user_interest_${Date.now()}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);

      toast.success(`Exported ${totalRows} records`, {
        position: "top-right",
        autoClose: 2000,
      });
    } catch (err) {
      toast.warning(err.message, {
        position: "top-right",
        autoClose: 2500,
      });
    } finally {
      setExportingAll(false);
    }
  };

  // ---- Filter handlers ----
  const handleFilterChange = (field, value) => {
    setFilters((prev) => ({ ...prev, [field]: value }));
  };

  const applyFilters = () => {
    setPage(0);
    fetchData(1, filters);
  };

  const clearFilters = () => {
    const emptyFilters = {
      country: "",
      platform: "",
      language: "",
      fromDate: "",
      toDate: "",
    };
    setFilters(emptyFilters);
    setPage(0);
    fetchData(1, emptyFilters);
  };

  return (
    <>
      <Sidebar />
      <Container className="content">
        <ToastContainer
          position="top-right"
          autoClose={5000}
          hideProgressBar={false}
        />
        <Card className="card">
          <Card.Header className="heading">
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
              className="inside-header"
            >
              <span>USER INTEREST</span>
              <span style={{ fontSize: "14px", fontWeight: "normal" }}>
                Total Results: {totalCount}
              </span>
            </div>
          </Card.Header>
          <Card.Body className="body">
            {/* ---- Filter Bar ---- */}
            <div
              style={{
                display: "flex",
                flexWrap: "wrap",
                gap: "10px",
                marginBottom: "15px",
                alignItems: "center",
              }}
            >
              <TextField
                select
                label="Country"
                SelectProps={{ native: true }}
                value={filters.country}
                onChange={(e) => handleFilterChange("country", e.target.value)}
                style={{ minWidth: 120 }}
              >
                <option value=""></option>
                {countryOptions.map((c, i) => (
                  <option key={i} value={c}>
                    {c}
                  </option>
                ))}
              </TextField>

              <TextField
                select
                label="Platform"
                SelectProps={{ native: true }}
                value={filters.platform}
                onChange={(e) => handleFilterChange("platform", e.target.value)}
                style={{ minWidth: 120 }}
              >
                <option value=""></option>
                {platformOptions.map((p, i) => (
                  <option key={i} value={p}>
                    {p}
                  </option>
                ))}
              </TextField>

              <TextField
                select
                label="Language"
                SelectProps={{ native: true }}
                value={filters.language}
                onChange={(e) => handleFilterChange("language", e.target.value)}
                style={{ minWidth: 120 }}
              >
                <option value=""></option>
                {languageOptions.map((l, i) => (
                  <option key={i} value={l}>
                    {l}
                  </option>
                ))}
              </TextField>

              <TextField
                label="From Date"
                type="date"
                InputLabelProps={{ shrink: true }}
                value={filters.fromDate}
                onChange={(e) => handleFilterChange("fromDate", e.target.value)}
              />

              <TextField
                label="To Date"
                type="date"
                InputLabelProps={{ shrink: true }}
                value={filters.toDate}
                onChange={(e) => handleFilterChange("toDate", e.target.value)}
              />

              <Button variant="primary" size="sm" onClick={applyFilters}>
                Apply Filters
              </Button>
              <Button variant="secondary" size="sm" onClick={clearFilters}>
                Clear
              </Button>

              {exportingAll && (
                <span
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "6px",
                    fontSize: "13px",
                  }}
                >
                  <span className="mini-spinner" />
                  Exporting...
                </span>
              )}
            </div>

            {loading ? (
              <Loader margin={"auto"} />
            ) : (
              <MaterialTable
                data={data}
                icons={tableIcons}
                columns={[
                  {
                    title: "#",
                    field: "id",
                    render: (rowData) => rowData.tableData.id + 1,
                    cellStyle: {
                      borderRight: "1px solid #e0e0e0",
                      borderLeft: "1px solid #e0e0e0",
                      wordBreak: "break-all",
                    },
                  },
                  {
                    title: "User_id",
                    field: "user_id",
                    cellStyle: {
                      borderRight: "1px solid #e0e0e0",
                      wordBreak: "break-all",
                    },
                  },
                  {
                    title: "Purpose",
                    field: "purpose",
                    cellStyle: {
                      borderRight: "1px solid #e0e0e0",
                      wordBreak: "break-all",
                    },
                  },
                  {
                    title: "Country_code",
                    field: "country_code",
                    cellStyle: {
                      borderRight: "1px solid #e0e0e0",
                    },
                  },
                  {
                    title: "Language_code",
                    field: "language_code",
                    cellStyle: {
                      borderRight: "1px solid #e0e0e0",
                    },
                  },
                  {
                    title: "App_version",
                    field: "app_version",
                    cellStyle: {
                      borderRight: "1px solid #e0e0e0",
                    },
                  },
                  {
                    title: "Platform",
                    field: "platform",
                    cellStyle: {
                      borderRight: "1px solid #e0e0e0",
                    },
                  },
                  {
                    title: "Message",
                    field: "message",
                    cellStyle: {
                      borderRight: "1px solid #e0e0e0",
                      wordBreak: "break-all",
                    },
                  },
                  {
                    title: "Added_date",
                    field: "formatted_date",
                    cellStyle: {
                      borderRight: "1px solid #e0e0e0",
                    },
                  },
                ]}
                options={{
                  pageSize: pageSize,
                  paginationType: "normal",
                  showTitle: false,
                  pagination: true,
                  pageSizeOptions: PAGE_SIZE_OPTIONS,
                  tableLayout: "auto",
                  search: false,
                  exportButton: true,
                  exportAllData: true,
                  // Override the built-in CSV export: renderData is only the
                  // current page, so we refetch every filtered record instead.
                  exportCsv: (columnList, renderData) => {
                    exportAllData(columnList);
                  },
                  rowStyle: (rowData, index) => {
                    return {
                      backgroundColor: index % 2 === 0 ? "#f9f9f9" : "#ffffff",
                    };
                  },
                }}
                totalCount={totalCount}
                page={page}
                onChangePage={(newPage) => handlePageChange(newPage)}
                onChangeRowsPerPage={(newPageSize) =>
                  handlePageSizeChange(newPageSize)
                }
                localization={{
                  pagination: {
                    labelRowsSelect: "rows",
                    labelDisplayedRows: "{from}-{to} of {count}",
                  },
                }}
              />
            )}
          </Card.Body>
        </Card>
      </Container>
    </>
  );
}

export default UserInterest;
